import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { User } from '../models/user';

import { catchError, map, Observable, of, retry } from 'rxjs';
import 'rxjs-compat/add/observable/of';
import { Constants } from '../constant';
import { Common } from '../../shared/common';
import { TokenStorageService } from '../token-storage.service';

@Injectable()
export class UserService {

  constructor(private http: HttpClient,
    private tokenStorageService: TokenStorageService
  ) { }

  getAllUsers() {
    return this.http.get<User>(Constants.getApiEndpoint() + '/user/all');
  }

  getImage(id) {
    return this.http.get((Constants.getApiEndpoint() + '/user/photos/' + id), { responseType: 'blob' });
  }

  getUsersByRollNo(rollNo) {
    return this.http.get<User>(Constants.getApiEndpoint() + '/user/getByEmployeeIdRoleId/' + rollNo);
  }

  searchStudents(student: string): Observable<any[]> {
    if (!student || student.length < 2) {
      return new Observable<any[]>(observer => {
        observer.next([]);
        observer.complete();
      });
    }
    return this.http.get<any[]>(Constants.getApiEndpoint() + '/user/searchStudentByRollNo/' + student).pipe(
      map((res: any) => {
        if (res.returnCode === 200 && res.result) {
          if (Array.isArray(res.result)) {
            return res.result;
          }
          return [res.result];
        }
        return [];
      })
    );
  }

  getUsersByType(type) {
    return this.http.get<User>(Constants.getApiEndpoint() + '/user/user/' + type);
  }

  getUsersList(type: string, pgIndex: number, pageSize: number, search?, stayIn?: string) {
    const options = stayIn ? Common.campusHeader(stayIn) : {};
    return this.http.get<User>(Constants.getApiEndpoint() + '/user/findByType/' + type + '?page=' + pgIndex + '&size=' + pageSize + '&search=' + search, options)
      .pipe(retry(1), catchError(Common.handleError));
  }

  getOverallCount(campus: string = 'MAIN') {
    return this.http.get<any>(Constants.getApiEndpoint() + '/user/overall_count', Common.campusHeader(campus)).pipe(retry(1), catchError(Common.handleError));
  }

  geStudentMovementStatus(campus: string = 'MAIN') {
    return this.http.get<any>(Constants.getApiEndpoint() + '/user/student_movement_count', Common.campusHeader(campus)).pipe(retry(1), catchError(Common.handleError));
  }

  geAllOutStudent(campus: string = 'MAIN') {
    return this.http.get<any>(Constants.getApiEndpoint() + '/user/get_out_student', Common.campusHeader(campus)).pipe(retry(1), catchError(Common.handleError));
  }

  loadUser(): Observable<any> {
    if (this.tokenStorageService.isAuthenticated()) {
      return this.getUser(this.tokenStorageService.getUser().id);
    } else {
      return of(null);
    }
  }

  getUser(id: string) {
    if (id === "new") {
      return Observable.of(this.initializeUser());
    };
    return this.http.get<User>(Constants.getApiEndpoint() + '/user/' + id).pipe(retry(1), catchError(Common.handleError));
  }

  deleteUserMaster(id: string) {
    return this.http.delete<User>(Constants.getApiEndpoint() + '/user/' + id).pipe(retry(1), catchError(Common.handleError));
  }

  saveUserMaster(formData: FormData, id?: string) {
    if (!id) {
      return this.http.post(
        Constants.getApiEndpoint() + '/user',
        formData
      );
    }

    return this.http.put(
      Constants.getApiEndpoint() + '/user',
      formData
    );
  }

  bulkUpdateApplications(rollNo: string, appNo: string, email: string) {
    return this.http.post(
      Constants.getApiEndpoint() + '/user/udpate-app-no' + `?rollNo=${encodeURIComponent(rollNo)}&appNo=${encodeURIComponent(appNo)}&email=${encodeURIComponent(email)}`,
      {}
    ).pipe(
      retry(1),
      catchError(Common.handleError)
    );
  }

  bulkUploadImages(formData: FormData) {
    return this.http.post(
      Constants.getApiEndpoint() + '/user/bulk-upload',
      formData
    ).pipe(
      retry(1),
      catchError(Common.handleError)
    );
  }

  updateUserStatus(userId: string | any[], status?: string) {
    let payload;
    if (Array.isArray(userId)) {
      payload = userId;
    } else {
      payload = [{ userId: userId, status: status }];
    }
    return this.http.put(
      Constants.getApiEndpoint() + '/user/status',
      payload
    );
  }

  private createUserMaster(user: User) {
    user.id = undefined;
    return this.http.post<User>(Constants.getApiEndpoint() + '/user', JSON.stringify(user), Common.httpOptions)
      .pipe(retry(1), catchError(Common.handleError));
  }

  private updateUserMaster(user: User) {
    return this.http.put<User>(Constants.getApiEndpoint() + '/user', JSON.stringify(user), Common.httpOptions)
      .pipe(retry(1), catchError(Common.handleError));
  }

  /* Bulk Image Upload Start */

  // Add to UserService class

  uploadBulkImages(formData: FormData): Observable<any> {
    return this.http.post(
      Constants.getApiEndpoint() + '/user/bulk-upload',
      formData,
      {
        reportProgress: true,
        observe: 'events'
      }
    ).pipe(
      map(event => this.getEventMessage(event)),
      catchError(Common.handleError)
    );
  }

  private getEventMessage(event: HttpEvent<any>) {
    switch (event.type) {
      case HttpEventType.UploadProgress:
        const percentDone = Math.round(100 * event.loaded / (event.total || 1));
        return { status: 'progress', percentDone };

      case HttpEventType.Response:
        return event.body;

      default:
        return `File upload event: ${event.type}`;
    }
  }

  getBulkUploads(page: number, size: number, search?: string): Observable<any> {
    let url = `${Constants.getApiEndpoint()}/user/bulk-uploads?page=${page}&size=${size}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    return this.http.get(url).pipe(retry(1), catchError(Common.handleError));
  }

  deleteBulkImage(id: string): Observable<any> {
    return this.http.delete(`${Constants.getApiEndpoint()}/user/bulk-image/${id}`)
      .pipe(retry(1), catchError(Common.handleError));
  }

  /* Bulk Image Upload End */

  getStudentCurfewViolationList(startDate: string, endDate: string, gender?: string) {
    let url =
      `${Constants.getApiEndpoint()}/curfew_violations/student-list` +
      `?startDate=${startDate}&endDate=${endDate}`;

    if (gender) {
      url += `&gender=${encodeURIComponent(gender)}`;
    }

    return this.http.get<any>(url).pipe(
      retry(1),
      catchError(Common.handleError)
    );
  }

  getStudentMajorCurfewViolationList(startDate: string, endDate: string, gender?: string
  ) {
    let url =
      `${Constants.getApiEndpoint()}/curfew_violations/major/student-list` +
      `?startDate=${startDate}&endDate=${endDate}`;

    if (gender) {
      url += `&gender=${encodeURIComponent(gender)}`;
    }

    return this.http.get<any>(url).pipe(
      retry(1),
      catchError(Common.handleError)
    );
  }



  initializeUser(): User {
    // Return an initialized object
    return {
      id: "",
      employeeId: "",
      name: "",
      email: "",
      mobile: "",
      password: "",
      cardNo: "",
      gender: "",
      roleId: "",
      batchId: undefined,
      expiryDate: new Date(),
      active: false
    };
  }
}
